import { useCallback, useState } from 'react'
import { validateDiscountCode } from 'services/plan'

export interface IAppliedDiscount {
    code: string
    percentage: number
}

/**
 * Amount left to pay after a discount.
 *
 * Deliberately mirrors Android's `Voucher.calculatePayableAmount`: take the
 * whole-toman discount first, then subtract. The backend checks the amount we
 * send against `int(price * (1 - pct/100))`, which rounds the *other* way, so
 * this form is always equal to or one toman above what the server will accept —
 * and the server only rejects underpayment. Computing it the server's way would
 * risk landing a toman short and getting the purchase refused.
 */
export const payableAfterDiscount = (
    amount: number,
    percentage: number
): number => amount - Math.floor((amount * percentage) / 100)

const messageFor = (error: any): string => {
    const status = error?.response?.status
    const detail: string = error?.response?.data?.detail ?? ''

    if (status === 404) return 'این کد تخفیف وجود ندارد'
    if (status === 403 && detail.includes('already used'))
        return 'این کد قبلاً استفاده شده است'
    if (status === 403) return 'این کد برای شما فعال نیست'
    return 'بررسی کد تخفیف ممکن نشد. دوباره تلاش کنید'
}

export default function useDiscount() {
    const [applied, setApplied] = useState<IAppliedDiscount | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [isChecking, setIsChecking] = useState(false)

    const clear = useCallback(() => {
        setApplied(null)
        setError(null)
    }, [])

    const apply = useCallback(async (rawCode: string) => {
        const code = rawCode.trim()
        if (!code) return

        setIsChecking(true)
        setError(null)
        try {
            const discount = await validateDiscountCode(code)

            // The endpoint's own expiry check is commented out server-side, so
            // an expired code still comes back 200. Catch it here rather than
            // letting the purchase fail at settlement.
            const expired =
                !!discount.valid_until &&
                new Date(discount.valid_until).getTime() < Date.now()

            if (!discount.is_active) {
                setApplied(null)
                setError('این کد فعال نیست')
            } else if (expired) {
                setApplied(null)
                setError('اعتبار این کد تمام شده است')
            } else if (!discount.percentage) {
                setApplied(null)
                setError('این کد تخفیفی روی این بسته ندارد')
            } else {
                setApplied({
                    code: discount.code,
                    percentage: discount.percentage,
                })
            }
        } catch (e) {
            setApplied(null)
            setError(messageFor(e))
        } finally {
            setIsChecking(false)
        }
    }, [])

    return { applied, error, isChecking, apply, clear }
}
